import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, FormError } from "@/components/FormField";
import { ImportantNote } from "@/components/ImportantNote";
import { t } from "@/lib/i18n";

const MIN_PASSWORD_LENGTH = 12;

export default function SignUpPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile: "", password: "", is_adult: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = t("auth.errors.firstName");
    if (!form.last_name.trim()) e.last_name = t("auth.errors.lastName");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = t("auth.errors.email");
    if (form.password.length < MIN_PASSWORD_LENGTH) e.password = t("auth.errors.passwordLength", { n: MIN_PASSWORD_LENGTH });
    if (!form.is_adult) e.is_adult = t("auth.errors.adult");
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    setFormError("");
    if (Object.keys(e).length) return;
    if (form.is_adult === "no") return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, mobile: form.mobile || null, is_adult: true });
      setUser(data.user);
      toast.success(t("auth.welcome", { name: data.user.first_name }));
      navigate("/portal");
    } catch (err) {
      setFormError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16" data-testid="sign-up-page">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <h1 className="text-4xl sm:text-5xl">{t("auth.signUpTitle")}</h1>
          <p className="mt-4 text-ink-500 max-w-[55ch]">{t("auth.signUpIntro")}</p>
          <form onSubmit={submit} noValidate className="mt-10 max-w-md" data-testid="sign-up-form">
            <FormError message={formError} />
            <Field label={t("auth.firstName")} required error={errors.first_name}>
              {(p) => <input {...p} className="input" autoComplete="given-name" value={form.first_name} onChange={set("first_name")} data-testid="sign-up-first-name" />}
            </Field>
            <Field label={t("auth.lastName")} required error={errors.last_name}>
              {(p) => <input {...p} className="input" autoComplete="family-name" value={form.last_name} onChange={set("last_name")} data-testid="sign-up-last-name" />}
            </Field>
            <Field label={t("auth.email")} required error={errors.email}>
              {(p) => <input {...p} type="email" className="input" autoComplete="email" value={form.email} onChange={set("email")} data-testid="sign-up-email" />}
            </Field>
            <Field label={t("auth.mobile")} help={t("auth.mobileHelp")}>
              {(p) => <input {...p} type="tel" className="input" autoComplete="tel" value={form.mobile} onChange={set("mobile")} data-testid="sign-up-mobile" />}
            </Field>
            <Field label={t("auth.password")} required help={t("auth.passwordHelp")} error={errors.password}>
              {(p) => <input {...p} type="password" className="input" autoComplete="new-password" value={form.password} onChange={set("password")} data-testid="sign-up-password" />}
            </Field>
            <fieldset className="mb-6" aria-describedby={errors.is_adult ? "adult-error" : undefined}>
              <legend className="label">{t("auth.adult")} <span className="text-ink-500 font-normal">(required)</span></legend>
              <div className="mt-2 flex flex-col gap-2">
                {[["yes", t("auth.adultYes")], ["no", t("auth.adultNo")]].map(([v, label]) => (
                  <label key={v} className="tap justify-start gap-3 rounded border border-sage-400 bg-cream-100 px-4 has-[:checked]:border-olive-900 has-[:checked]:ring-2 has-[:checked]:ring-olive-900/20 cursor-pointer">
                    <input type="radio" name="is_adult" value={v} checked={form.is_adult === v} onChange={set("is_adult")} className="h-5 w-5 accent-[#212B23]" data-testid={`sign-up-adult-${v}`} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              {errors.is_adult && <p id="adult-error" className="field-error" role="alert">{errors.is_adult}</p>}
              {form.is_adult === "no" && <p className="surface mt-3 p-4 text-ink-900" role="status" data-testid="sign-up-minor-notice">{t("auth.adultNoExplain")}</p>}
            </fieldset>
            <button type="submit" className="btn-primary w-full" disabled={busy || form.is_adult === "no"} data-testid="sign-up-submit">{busy ? t("common.loading") : t("auth.createAccount")}</button>
            <p className="mt-6 text-ink-500">{t("auth.haveAccount")} <Link to="/sign-in" className="link" data-testid="sign-up-to-sign-in">{t("auth.signIn")}</Link></p>
            <p className="mt-4 text-sm text-ink-500">By creating an account you agree to our <Link to="/terms" className="link font-normal">Terms</Link> and have read <Link to="/privacy" className="link font-normal">How We Protect Your Information</Link>.</p>
          </form>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <ImportantNote />
        </div>
      </div>
    </div>
  );
}
